<script setup lang="ts">
import { useElementSize } from "@vueuse/core";
import {
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  watch,
} from "vue";
import ErrorPage from "./errorPage.vue";
import { uuid } from "./tzj-player";
// 萤石云播放器
defineOptions({
  name: "YsPlayer",
});

// 传值方式
// 1、传url、accessToken
// 2、传deviceSerial、channel、accessToken
const props = defineProps<{
  // 播放地址
  // 高清：ezopen://open.ys7.com/${设备序列号}/${通道号}.hd.live
  // 流畅：ezopen://open.ys7.com/${设备序列号}/${通道号}.live
  url?: string;
  // 设备序列号
  deviceSerial?: string;
  // 通道号
  channel?: number;
  // 清晰度,默认为空，也表示高清
  // hd：高清；sd：流畅
  quality?: "hd" | "sd" | "";
  accessToken: string;
  // 播放器模板ID
  playerTemplate?: string;
  // 设备名称
  areaName?: string;
}>();

// 播放器模板ID
// 可在萤石云控制台查看、修改、获取
// 位置：云直播-轻应用-特致珈默认模板
const PLAYER_TEMPLATE = "5f295a9341a74d439b5d69374c90d501";
const id = uuid();
let playerInstance: any = null;
let errorMsg = "";
const ysRef = shallowRef();
const containerRef = shallowRef();
const { width, height } = useElementSize(ysRef);
const { width: childWidth, height: childHeight } = useElementSize(containerRef);
let ratio = 16 / 9;
let isCurrentFullscreen = false;

// 添加重连次数控制，防止无限重连
const reconnectAttempts = ref(0);
const maxReconnectAttempts = 3;

// 添加防抖处理URL变化
const debouncedPlay = debounce(() => {
  play();
}, 500);

// 监听URL变化，使用防抖处理
watch(
  () => props.url,
  () => {
    debouncedPlay();
  },
);

onMounted(() => play());

onBeforeUnmount(() => destroy());

defineExpose({ play, destroy, resize });

// 防抖函数
function debounce(func: Function, wait: number) {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return function (this: any, ...args: any[]) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

// 加上防抖的 resize 函数
const debouncedResize = debounce(resize, 300);

watch(
  () => [width.value, height.value, ratio],
  () => {
    if (!isCurrentFullscreen) {
      debouncedResize();
    }
  },
);

async function play(options?: Record<string, any>) {
  if (!props.accessToken) {
    errorMsg = `params 'accessToken' is not found`;
  } else {
    const url = formatterUrl();
    if (url) {
      createPlayerDom();
      await nextTick();
      await createPlayer(options);
    }
  }
}

function destroy() {
  if (playerInstance) {
    try {
      // 确保播放器实例完全销毁
      playerInstance.destroy?.();
    } catch (e) {
      console.warn("播放器销毁过程中出现错误:", e);
    }
    playerInstance = null;
    removePlayerDom();
  }
}

function resize() {
  const containerWidth = childWidth.value;
  const containerHeight = childHeight.value;
  // 如果容器尺寸无效，直接返回
  if (containerWidth <= 0 || containerHeight <= 0) return;

  // 计算播放器尺寸，保持宽高比

  const aspectRatio = ratio;

  let playerWidth = containerWidth;
  let playerHeight = playerWidth / aspectRatio;

  // 如果计算出的高度超过容器高度，以高度为准重新计算
  if (playerHeight > containerHeight) {
    playerHeight = containerHeight;
    playerWidth = playerHeight * aspectRatio;
  }

  // 尝试使用播放器实例的resize方法
  if (playerInstance?.resize) {
    playerInstance.resize(playerWidth, playerHeight);
    return;
  }

  // 如果没有播放器实例，直接操作DOM
  if (typeof document !== "undefined") {
    const playerElement = document.getElementById(id);
    if (playerElement) {
      playerElement.style.width = `${playerWidth}px`;
      playerElement.style.height = `${playerHeight}px`;
    }
  }
}

/**
 * 创建萤石云播放器实例，播放萤石云直播流
 */
async function createPlayer(options?: Record<string, any>) {
  try {
    // 动态导入 EZUIKit，避免 SSR 环境下的 window 访问错误
    const EZUIKit = await import("ezuikit-js");

    playerInstance = new EZUIKit.default.EZUIKitPlayer({
      id,
      url: formatterUrl(),
      accessToken: props.accessToken,
      template: props.playerTemplate || PLAYER_TEMPLATE,
      width: width.value,
      height: height.value,
      audio: 1,
      // @ts-ignore
      loggerOptions: {
        name: "YSPLAYER",
        level: "ERROR",
        showTime: true,
      },
      staticPath: "/plugins/ezuikit/ezuikit_static",
      ...options,
    });

    // 监听播放错误事件，限制重连次数
    playerInstance.on("error", (err: any) => {
      console.error("萤石云播放器错误:", err);
      // 控制重连次数
      if (reconnectAttempts.value < maxReconnectAttempts) {
        reconnectAttempts.value++;
        setTimeout(() => {
          destroy();
          play();
        }, 3000 * reconnectAttempts.value);
      }
    });
  } catch (error) {
    console.error("Failed to load EZUIKit:", error);
    errorMsg = "播放器加载失败";
    return;
  }

  playerInstance.eventEmitter.on("fullscreen", () => {
    isCurrentFullscreen = true;
  });
  playerInstance.eventEmitter.on("exitFullscreen", () => {
    isCurrentFullscreen = false;
  });
  playerInstance.eventEmitter.on("videoInfo", (args: any) => {
    const { width: w, height: h } = args;
    // 计算播放器宽高比
    if (w && h) {
      ratio = w / h;
    }
    debouncedResize();
  });
}

function formatterUrl(): string {
  if (props.url) {
    return props.url;
  } else {
    if (props.deviceSerial && props.channel) {
      const quality =
        props.quality === "" || props.quality === "hd" ? "hd." : "";
      return `ezopen://open.ys7.com/${props.deviceSerial}/${props.channel}.${quality}live`;
    } else {
      errorMsg = `params 'deviceSerial' or 'channel' is not found`;
      return "";
    }
  }
}

// 移除播放器DOM
function removePlayerDom() {
  if (typeof document !== "undefined") {
    const dom = document.getElementById(id);
    dom && dom.remove();
  }
}
// 创建播放器DOM
function createPlayerDom() {
  if (!errorMsg) {
    removePlayerDom();
    if (typeof document !== "undefined") {
      const dom = document.createElement("div");
      dom.id = id;
      dom.style.width = "100%";
      dom.style.height = "100%";
      containerRef.value?.appendChild(dom);
    }
  }
}
</script>

<template>
  <div ref="ysRef" class="i-tzj-ysplayer">
    <div
      ref="containerRef"
      class="i-tzj-ysplayer"
      style="width: 100%; height: 100%; position: relative"
    />
    <ErrorPage v-if="errorMsg" :message="errorMsg" />
  </div>
</template>
